import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { useHistory } from "react-router-dom";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const InternalChatModal = ({ type, open, onClose, onSuccess, user }) => {
  const history = useHistory();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [groupName, setGroupName] = useState("");
  const [groupMembers, setGroupMembers] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchUsers = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data.users || []);
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    if (open) fetchUsers();
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSelectedUser(null);
      setGroupName("");
      setGroupMembers([]);
    }
  }, [open]);

  const handleCreateDM = async () => {
    if (!selectedUser) return;
    setLoading(true);
    try {
      const { data } = await api.post("/internal-chats/dm", {
        userId: selectedUser.id,
      });
      onClose();
      if (onSuccess) onSuccess();
      history.push(`/internal-chats/${data.id}`);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim() || groupMembers.length === 0) return;
    setLoading(true);
    try {
      const { data } = await api.post("/internal-chats/group", {
        name: groupName.trim(),
        userIds: groupMembers.map((u) => u.id),
      });
      onClose();
      if (onSuccess) onSuccess();
      history.push(`/internal-chats/${data.id}`);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const otherUsers = users.filter((u) => u.id !== user?.id);

  if (type === "dm") {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{i18n.t("internalChat.dmModal.title")}</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={otherUsers}
            getOptionLabel={(option) => option.name}
            value={selectedUser}
            onChange={(event, newValue) => setSelectedUser(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={i18n.t("internalChat.dmModal.userLabel")}
                variant="outlined"
                fullWidth
                margin="normal"
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{i18n.t("internalChat.buttons.cancel")}</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleCreateDM}
            disabled={!selectedUser || loading}
          >
            {i18n.t("internalChat.buttons.start")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{i18n.t("internalChat.groupModal.title")}</DialogTitle>
      <DialogContent>
        <TextField
          label={i18n.t("internalChat.groupModal.nameLabel")}
          variant="outlined"
          fullWidth
          margin="normal"
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
        />
        <Autocomplete
          multiple
          options={otherUsers}
          getOptionLabel={(option) => option.name}
          value={groupMembers}
          onChange={(event, newValue) => setGroupMembers(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label={i18n.t("internalChat.groupModal.membersLabel")}
              variant="outlined"
              fullWidth
              margin="normal"
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{i18n.t("internalChat.buttons.cancel")}</Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleCreateGroup}
          disabled={!groupName.trim() || groupMembers.length === 0 || loading}
        >
          {i18n.t("internalChat.buttons.create")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InternalChatModal;
